import { FieldArray, FieldArrayRenderProps, Input, Toggle } from 'components/form';
import { YES_NO_OPTIONS } from 'constants/form';
import { useFormSubField } from 'hooks/form';
import isEqual from 'lodash/isEqual';
import { _TargetedTherapyInput } from 'models/endpoint';
import React from 'react';
import { useTranslation } from 'react-i18next';
import AddItem from '../AddItem';
import RemoveItem from '../RemoveItem';

export type TargetedTherapy = _TargetedTherapyInput;
export const DEFAULT_TARGETED_THERAPY: TargetedTherapy = {
  id: undefined,
  targeted_therapy: '',
  date_to: '',
  date_from: '',
  record_type: ''
};

interface TargertedTherapyItemProps {
  index: number;
  helpers: FieldArrayRenderProps;
  showAdd: boolean;
  showRemove: boolean;
  onChangeCb: (data?: any) => void;
}

const TargertedTherapyItem: React.FC<TargertedTherapyItemProps> = React.memo(
  ({ index, helpers, showAdd, showRemove, onChangeCb }) => {
    const [t] = useTranslation();
    const { getSubName } = useFormSubField('targeted_therapies');
    const insert = () => helpers.push(DEFAULT_TARGETED_THERAPY);
    const remove = () => {
      helpers.remove(index);
      onChangeCb();
    };
    return (
      <React.Fragment>
        <div className="flex-row">
          {index === 0 ? (
            <div className="flex-child">
              <Toggle
                name={getSubName('targeted_therapy', index)}
                items={YES_NO_OPTIONS}
                onChange={onChangeCb}
                fullWidth
              />
            </div>
          ) : null}
          <div className="flex-child" style={{ flexGrow: index === 0 ? 3 : 1 }}>
            <div className="flex-row">
              <div className="flex-child">
                <Input type="date" label={t('from')} name={getSubName('date_from', index)} fullWidth />
              </div>
              <div className="flex-child">
                <Input type="date" label={t('to')} name={getSubName('date_to', index)} fullWidth />
              </div>
            </div>
          </div>
        </div>
        <div className="flex-row">
          <Input label={t('type')} name={getSubName('record_type', index)} fullWidth />
        </div>
        <div className="add-multiple">
          {showAdd ? <AddItem onClick={insert} /> : null}
          {showRemove ? <RemoveItem onClick={remove} /> : null}
        </div>
      </React.Fragment>
    );
  }
);

interface Props {
  list: TargetedTherapy[];
  onChangeCb: (data?: any) => void;
}

const HormonalTherapy: React.FC<Props> = props => {
  const [t] = useTranslation('patient_profile');
  const { getName } = useFormSubField('targeted_therapies');
  const { list, onChangeCb } = props;
  const totalItems = list.length;
  const lastItemIndex = totalItems - 1;
  return (
    <div className="section">
      <label className="sub-header grey">{t('targeted_therapy', { count: 1 })}</label>
      <FieldArray
        name={getName()}
        render={helpers =>
          list.map((_, index: number) => (
            <TargertedTherapyItem
              key={index}
              index={index}
              helpers={helpers}
              showAdd={totalItems === 1 || (totalItems > 1 && lastItemIndex === index)}
              showRemove={index > 0 && lastItemIndex === index}
              onChangeCb={onChangeCb}
            />
          ))
        }
      />
    </div>
  );
};

export default React.memo(HormonalTherapy, (prev, next) => isEqual(prev.list, next.list));
